// src/components/modals/CustomerDetailModal.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  Phone,
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  Edit2,
  Save,
  Trash2,
  Calendar,
  Lock,
  LockOpen,
  Info,
  User,
  AlertTriangle
} from 'lucide-react';
import Modal from '../ui/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import CountryPhoneInput from '../ui/CountryPhoneInput';
import ReportCustomerModal from './ReportCustomerModal';
import CustomerAppointmentsModal from './CustomerAppointmentsModal';
import AppointmentItem from '../customers/AppointmentItem';
import TechnicalNotesPanel from '../customers/TechnicalNotesPanel';

import { useAuth } from '@/contexts/AuthContext';
import { useClients } from '@/contexts/ClientsContext';
import { useEvents } from '@/contexts/EventsContext';
import { useUsers } from '@/contexts/UsersContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import { validateName, validatePhone, validateEmail } from '@/lib/validator';
import { getWhatsAppUrl } from '@/utils/whatsapp';

export interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isEmailVerified: boolean;
  totalAppointments: number;
  lastVisit: string;
  avatarColor: string;
  notes?: string;
  isManuallyEntry?: boolean;
  isBlocked?: boolean;
  avatar?: string;
  merged_into_id?: string | null;
}

interface CustomerDetailModalProps {
  customer: Customer | null;
  onClose: () => void;
}

type ConfirmAction = 'block' | 'unblock' | 'delete' | 'reset_reports' | null;

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  customer,
  onClose,
}) => {
  const { t } = useTranslation();
  const { organization } = useAuth();

  const {
    updateClient,
    deleteClient,
    blockClient,
    unlockClient,
    blackList,
    fetchBlackList,
    customerReports: allCustomerReports,
    createCustomerReport,
    resetCustomerReports,
  } = useClients();

  const { bookings, getServiceById, getPerformanceById } = useEvents();
  const { getUserById } = useUsers();

  const clientBookings = useMemo(() => {
    if (!customer?.id || !bookings) return [];
    return [...bookings]
      .filter((b: any) => b.client_id === customer.id)
      .sort((a: any, b: any) => {
        const dateA = new Date(`${a.date}T${a.time || '00:00:00'}`);
        const dateB = new Date(`${b.date}T${b.time || '00:00:00'}`);
        return dateB.getTime() - dateA.getTime();
      });
  }, [customer?.id, bookings]);

  const customerReports = useMemo(() => {
    if (!customer?.id || !allCustomerReports) return [];
    return allCustomerReports.filter((r: any) => r.client_id === customer.id);
  }, [customer?.id, allCustomerReports]);

  const maxReportsLimit = organization?.max_reports_limit ?? 3;
  const willBlockOnNextReport = customerReports.length >= maxReportsLimit - 1;

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isAppointmentsModalOpen, setIsAppointmentsModalOpen] = useState(false);

  // Stati form
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    if (customer) {
      setIsEditing(false);
      setIsSaving(false);
      setIsActionLoading(false);
      setConfirmAction(null);
      setIsAppointmentsModalOpen(false);

      setDraftFirstName(customer.firstName || '');
      setDraftLastName(customer.lastName || '');
      setDraftEmail(customer.email || '');
      setDraftPhone(customer.phone || '');
      setDraftNotes(customer.notes || '');
    }
  }, [customer]);

  if (!customer) return null;

  const isBlocked = blackList?.includes(customer.id) ?? customer.isBlocked ?? false;
  const isVerifiedAppUser = customer.isEmailVerified || !customer.isManuallyEntry;

  const handleReportConfirm = async (reason: string) => {
    if (!customer?.id) return;
    setIsReporting(true);

    try {
      const result = await createCustomerReport({
        client_id: customer.id,
        reason,
      });

      if (result.success) {
        toast.success(t('bookings.modal.report_success', 'Cliente segnalato con successo'));

        if (willBlockOnNextReport) {
          await fetchBlackList();
        }
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      console.error(error);
      toast.error(t('common.error'));
    } finally {
      setIsReporting(false);
      setIsReportModalOpen(false);
    }
  };

  // EDIT / SAVE LOGIC
  const handleToggleEdit = async () => {
    if (isVerifiedAppUser && !isEditing) {
      toast.info(t('customers.modal.verified_edit_disabled', "Non puoi modificare i dati di un utente verificato."));
      return;
    }

    if (!isEditing) {
      // Reset stati quando entri in edit per sicurezza
      setDraftFirstName(customer.firstName || '');
      setDraftLastName(customer.lastName || '');
      setDraftEmail(customer.email || '');
      setDraftPhone(customer.phone || '');
      setDraftNotes(customer.notes || '');
      setIsEditing(true);
      return;
    }

    if (isSaving) return;

    if (!draftFirstName.trim()) {
      toast.error(t('customers.modal.toast.name_required', 'Il nome è obbligatorio'));
      return;
    }

    if (!draftPhone.trim()) {
      toast.error(t('customers.modal.toast.phone_required', 'Il telefono è obbligatorio'));
      return;
    }

    const firstNameValidation = validateName(draftFirstName, 'Nome');
    if (!firstNameValidation.isValid) {
      toast.error(firstNameValidation.message);
      return;
    }

    if (draftLastName.trim()) {
      const lastNameValidation = validateName(draftLastName, 'Cognome');
      if (!lastNameValidation.isValid) {
        toast.error(lastNameValidation.message);
        return;
      }
    }

    if (draftEmail.trim()) {
      const emailValidation = validateEmail(draftEmail);
      if (!emailValidation.isValid) {
        toast.error(emailValidation.message);
        return;
      }
    }

    const phoneValidation = validatePhone(draftPhone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.message);
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        first_name: draftFirstName.trim(),
        last_name: draftLastName.trim(),
        email: draftEmail.trim() || '',
        phone: draftPhone.trim() || '',
        notes: draftNotes.trim() || null,
      };

      const res = await updateClient(customer.id, payload);

      if (!res || !res.success) {
        toast.error(res?.error || t('customers.modal.toast.update_error'));
        setIsSaving(false);
        return;
      }

      toast.success(t('customers.modal.toast.update_success'));
      setIsSaving(false);
      setIsEditing(false);
      onClose();
    } catch (err: any) {
      console.error('Errore updateClient:', err);
      toast.error(err?.message || t('customers.modal.toast.update_error'));
      setIsSaving(false);
    }
  };

  // ACTION LOGIC
  const handleConfirmAction = async () => {
    if (!confirmAction || !customer) return;

    setIsActionLoading(true);
    try {
      if (confirmAction === 'delete') {
        const result = await deleteClient(customer.id);
        if (!result || !result.success) {
          toast.error(result?.error || t('customers.modal.toast.delete_error'));
          setIsActionLoading(false);
          return;
        }
        toast.success(t('customers.modal.toast.delete_success'));
      }

      if (confirmAction === 'block') {
        const res = await blockClient(customer.id);
        if (!res || !res.success) {
          toast.error(res?.error || t('customers.modal.toast.block_error'));
          setIsActionLoading(false);
          return;
        }
        toast.success(t('customers.modal.toast.block_success'));
      }

      if (confirmAction === 'unblock') {
        const res = await unlockClient(customer.id);
        if (!res || !res.success) {
          toast.error(res?.error || t('customers.modal.toast.unblock_error'));
          setIsActionLoading(false);
          return;
        }
        toast.success(t('customers.modal.toast.unblock_success'));
      }

      if (confirmAction === 'reset_reports') {
        const res = await resetCustomerReports(customer.id);
        if (!res || !res.success) {
          toast.error(res?.error || t('common.error'));
          setIsActionLoading(false);
          return;
        }
        toast.success(t('bookings.modal.report_reset_success', 'Segnalazioni azzerate con successo'));
      }

      setIsActionLoading(false);
      setConfirmAction(null);
      if (confirmAction !== 'reset_reports') {
        onClose();
      }
    } catch (err: any) {
      console.error('Errore azione cliente:', err);
      toast.error(err?.message || t('customers.modal.toast.op_error'));
      setIsActionLoading(false);
    }
  };

  let confirmTitle = t('customers.modal.confirm_title');
  let confirmDescription = '';
  let confirmLabel = '';

  if (confirmAction === 'block') {
    confirmTitle = t('customers.modal.block_title', "Blocca Cliente");
    confirmDescription = t('customers.modal.block_confirm_desc', { name: `${customer.firstName} ${customer.lastName}` });
    confirmLabel = t('customers.modal.block');
  } else if (confirmAction === 'unblock') {
    confirmTitle = t('customers.modal.unblock_title', "Sblocca Cliente");
    confirmDescription = t('customers.modal.unblock_confirm_desc', { name: `${customer.firstName} ${customer.lastName}` });
    confirmLabel = t('customers.modal.unblock');
  } else if (confirmAction === 'delete') {
    confirmTitle = t('customers.modal.delete_title', "Elimina Cliente");
    confirmDescription = t('customers.modal.delete_confirm_desc', { name: `${customer.firstName} ${customer.lastName}` });
    confirmLabel = t('customers.modal.delete');
  } else if (confirmAction === 'reset_reports') {
    confirmTitle = t('customers.modal.reset_reports_title', "Azzera Segnalazioni");
    confirmDescription = t('customers.modal.reset_reports_confirm_desc', { name: `${customer.firstName} ${customer.lastName}` });
    confirmLabel = t('customers.modal.reset');
  }

  const isGlobalDisabled = isSaving || isActionLoading;

  return (
    <>
      <Modal
        isOpen={!!customer}
        onClose={onClose}
        compact
        headerClassName="bg-gradient-to-br from-gray-900 to-gray-800 text-white"
        headerContent={
          <div className="flex items-center gap-3 pr-10">
            <div className="relative">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center text-base font-bold uppercase shadow ${customer.avatarColor} text-gray-800 bg-opacity-90`}
              >
                {((customer.firstName?.[0] || '') + (customer.lastName?.[0] || '')) || '?'}
              </div>

              {isBlocked && (
                <div className="absolute -top-1.5 -right-1.5 bg-red-500 text-white p-1 rounded-full shadow-md border-2 border-gray-800 z-10">
                  <Lock size={10} />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white truncate">
                {`${customer.firstName || ''} ${customer.lastName || ''}`.trim() || t('customers.modal.no_name', 'Cliente senza nome')}
              </h2>
              <p className="text-gray-300 flex items-center gap-1.5 text-xs opacity-80">
                <Calendar size={12} /> {t('customers.last_visit')}:{' '}
                {customer.lastVisit}
              </p>
            </div>
          </div>
        }
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900">
            {t('customers.modal.title')}
          </h3>

          {!customer.merged_into_id ? (
            !isVerifiedAppUser ? (
              <button
                onClick={handleToggleEdit}
                disabled={isGlobalDisabled || (isEditing && (!draftFirstName.trim() || !draftPhone.trim()))}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isEditing
                  ? 'bg-green-50 text-green-600 hover:bg-green-100'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
              >
                {isEditing ? <Save size={16} /> : <Edit2 size={16} />}
                {isEditing ? t('common.save') : t('common.edit')}
              </button>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-lg border border-blue-100 cursor-help" title="I dati anagrafici sono gestiti dall'utente">
                <Info size={14} />
                <span className="font-medium">Account Verificato</span>
              </div>
            )
          ) : null}
        </div>

        <div className="space-y-6">

          {customer.merged_into_id && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <Info size={20} className="text-yellow-600" />
              </div>
              <div>
                <h4 className="text-yellow-900 font-bold text-sm">
                  {t('customers.modal.archived_title', 'Cliente Archiviato')}
                </h4>
                <p className="text-yellow-700 text-xs mt-0.5">
                  {t('customers.modal.archived_description', 'Questo profilo è stato unito ad un altro. Non è possibile modificare o eliminare i dati da questa vista.')}
                </p>
              </div>
            </div>
          )}

          {/* Banner Segnalazioni e Blocco */}
          {isBlocked ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} className="text-red-500" />
              </div>
              <div>
                <h4 className="text-red-900 font-bold text-sm">
                  {t('bookings.modal.client_blocked_title', 'Attenzione: Cliente bloccato')}
                </h4>
                <p className="text-red-700 text-xs mt-0.5">
                  {t('bookings.modal.client_blocked_desc', 'Questo cliente è bloccato e non può effettuare prenotazioni')}
                </p>
              </div>
            </div>
          ) : customerReports.length > 0 ? (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-3 mb-6">
              <div className="flex gap-3 items-center">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={20} className="text-orange-500" />
                </div>
                <div>
                  <h4 className="text-orange-900 font-bold text-sm">
                    {t('bookings.modal.report_warning', 'Attenzione: Cliente segnalato')} ({customerReports.length}/{maxReportsLimit})
                  </h4>
                  <p className="text-orange-700 text-xs mt-0.5">
                    {t('bookings.modal.report_reason', 'Motivo della segnalazione:')} <span className="font-semibold italic">"{customerReports.find(r => r.reason)?.reason || t('bookings.modal.report_no_reason', 'Nessun motivo specificato')}"</span>
                  </p>
                </div>
              </div>

              <button
                onClick={() => setConfirmAction('reset_reports')}
                disabled={isGlobalDisabled}
                className="flex-shrink-0 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-xs font-bold hover:bg-orange-200 transition-colors flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                {t('common.delete', 'Elimina')}
              </button>
            </div>
          ) : null}

          {/* Nome & Cognome Fields */}
          <div className="grid grid-cols-2 gap-4">
            {/* Nome Field */}
            <div className="group">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                {t('customers.modal.name')}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={draftFirstName}
                  onChange={(e) => setDraftFirstName(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none transition-all"
                  placeholder={t('customers.modal.name')}
                />
              ) : (
                <div className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-transparent transition-all ${!isVerifiedAppUser && 'group-hover:border-gray-200'}`}>
                  <User className="text-gray-400" size={20} />
                  <span className="text-gray-700 font-medium">
                    {customer.firstName || 'N/A'}
                  </span>
                </div>
              )}
            </div>

            {/* Cognome Field */}
            <div className="group">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                {t('customers.modal.surname')}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={draftLastName}
                  onChange={(e) => setDraftLastName(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none transition-all"
                  placeholder={t('customers.modal.surname')}
                />
              ) : (
                <div className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-transparent transition-all ${!isVerifiedAppUser && 'group-hover:border-gray-200'}`}>
                  <User className="text-gray-400" size={20} />
                  <span className="text-gray-700 font-medium">
                    {customer.lastName || 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div className="group">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
              {t('customers.modal.email')}
            </label>
            {isEditing ? (
              <input
                type="email"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none transition-all"
              />
            ) : (
              <div className={`flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-transparent transition-all ${!isVerifiedAppUser && 'group-hover:border-gray-200'}`}>
                <Mail className="text-gray-400" size={20} />
                <span className="text-gray-700 font-medium">
                  {customer.email || 'N/A'}
                </span>
                {customer.email && (customer.isEmailVerified ? (
                  <span className="ml-auto hidden sm:flex items-center gap-1 text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded-md">
                    <CheckCircle2 size={12} /> {t('customers.verified')}
                  </span>
                ) : (
                  <span className="ml-auto hidden sm:flex items-center gap-1 text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded-md">
                    <AlertCircle size={12} /> {t('customers.not_verified')}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
              {t('customers.modal.phone')} *
            </label>
            {isEditing ? (
              <div className="relative">
                <CountryPhoneInput
                  value={draftPhone}
                  onChange={(val) => setDraftPhone(val)}
                  placeholder="+39 333 123 4567"
                />
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Phone className="text-gray-400" size={20} />
                <span className="text-gray-700 font-medium flex-1">
                  {customer.phone || 'N/A'}
                </span>
                {getWhatsAppUrl(customer.phone) && (
                  <a
                    href={getWhatsAppUrl(customer.phone)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Scrivi su WhatsApp"
                    className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-bold px-3 py-1.5 rounded-lg transition-colors active:scale-95"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Note tecniche: sta con gli altri campi, subito sotto il telefono */}
          <TechnicalNotesPanel clientId={customer.id} />

          {/* Storico Appuntamenti (Preview) */}
          {!isEditing && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">
                  {t('customers.appointments', 'Storico Appuntamenti')} {clientBookings.length > 0 && `(${clientBookings.length})`}
                </label>
                {clientBookings.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setIsAppointmentsModalOpen(true)}
                    className="text-xs font-bold text-clip-600 hover:text-clip-700 transition-colors"
                  >
                    {t('common.view_all')}
                  </button>
                )}
              </div>

              {clientBookings.length === 0 ? (
                <div className="text-center py-6 rounded-xl border border-dashed border-gray-300">
                  <p className="text-xs text-gray-400">{t('customers.modal.no_appointments')}</p>
                </div>
              ) : (
                <div>
                  <div className="space-y-2 pr-1">
                    {clientBookings.slice(0, 2).map((b, index) => (
                      <AppointmentItem
                        key={b.id}
                        booking={b}
                        getPerformanceById={getPerformanceById}
                        className={index === 1 ? 'opacity-30 select-none pointer-events-none' : ''}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer / Danger Zone */}
        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="text-xs text-gray-400 whitespace-nowrap">
            {t('customers.modal.customer_since')}:{' '}
            <span className="font-semibold text-gray-600">
              24 Gen 2021
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
            {!customer.merged_into_id && (
              <>
                {!isBlocked && customerReports.length < maxReportsLimit && (
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm font-semibold text-orange-500 hover:text-orange-700 hover:bg-orange-50 whitespace-nowrap"
                    disabled={isGlobalDisabled}
                    onClick={(e) => { e.stopPropagation(); setIsReportModalOpen(true); }}
                  >
                    <AlertTriangle size={16} />
                    {willBlockOnNextReport ? t('bookings.modal.report_and_block_btn', 'Segnala e blocca') : t('bookings.modal.report_btn', 'Segnala')}
                  </button>
                )}

                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm font-semibold whitespace-nowrap ${isBlocked ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'text-red-500 hover:text-red-700 hover:bg-red-50'}`}
                  disabled={isGlobalDisabled}
                  onClick={() => setConfirmAction(isBlocked ? 'unblock' : 'block')}
                >
                  {isBlocked ? <LockOpen size={16} /> : <Lock size={16} />}
                  {isBlocked ? t('customers.modal.unblock', 'Sblocca') : t('customers.modal.block', 'Blocca')}
                </button>

                {!isVerifiedAppUser && (
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all text-sm font-semibold text-red-500 hover:text-red-700 hover:bg-red-50 whitespace-nowrap"
                    disabled={isGlobalDisabled}
                    onClick={() => setConfirmAction('delete')}
                  >
                    <Trash2 size={16} />
                    {t('customers.modal.delete', 'Elimina')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        onConfirm={handleConfirmAction}
        onCancel={() => !isActionLoading && setConfirmAction(null)}
        loading={isActionLoading}
        variant={confirmAction === 'unblock' ? 'default' : 'danger'}
      />

      <ReportCustomerModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onConfirm={handleReportConfirm}
        clientName={`${customer?.firstName} ${customer?.lastName}`}
        isLoading={isReporting}
      />

      {isAppointmentsModalOpen && (
        <CustomerAppointmentsModal
          isOpen={isAppointmentsModalOpen}
          onClose={() => setIsAppointmentsModalOpen(false)}
          customer={customer}
          clientBookings={clientBookings}
          getPerformanceById={getPerformanceById}
          getServiceById={getServiceById}
        />
      )}
    </>
  );
};

export default CustomerDetailModal;